import {
  compareDesc,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  isFirstDayOfMonth,
  parse,
  startOfMonth,
  startOfWeek,
  startOfYear,
  getISOWeek,
} from "date-fns";
import { DSTChange } from "./countries";

export class DateTimeManager {
  private static _dstChanges: number | Array<DSTChange>;
  private static _hasDST: boolean;
  private static _instance: DateTimeManager;
  private constructor() {}
  static init(dstChanges: number | Array<DSTChange>) {
    DateTimeManager._dstChanges = dstChanges;
    if (typeof dstChanges === "object") {
      DateTimeManager._hasDST = true;
    }
    if (!DateTimeManager._instance) {
      DateTimeManager._instance = new DateTimeManager();
    }
  }

  static updateTimeOffset(date: Date | string): Date {
    if (typeof date === "string") {
      date = new Date(date);
    }
    return DateTimeManager._updateTimeOffset(date, DateTimeManager.getTimeOffsetForDate(date));
  }

  static get hasDST() {
    return DateTimeManager._hasDST;
  }
  static get offset() {
    return DateTimeManager._dstChanges;
  }

  private static getTimeOffsetForDate(date: Date): number {
    if (!DateTimeManager.hasDST) {
      return <number>DateTimeManager.offset;
    }
    if (DateTimeManager.offset && typeof DateTimeManager.offset !== "number") {
      // tslint:disable-next-line:max-line-length
      const index = DateTimeManager.offset.findIndex(
        (dateTime) => DateTimeManager.compareDates(date, new Date(dateTime.date)) === 1
      );
      if (index !== -1) {
        if (index === 0) {
          return DateTimeManager.offset[index].offset;
        }
        return DateTimeManager.offset[index - 1].offset;
      }
    }
    return Math.abs(date.getTimezoneOffset() / 60);
  }

  static getISODateFormat(date: Date): string {
    date.setTime(date.getTime() - DateTimeManager.getTimeOffsetForDate(date) * 3600000);
    return format(date, "yyyy-MM-dd'T'HH:mm:ss");
  }
  static updateTimePortion(date: Date, time: Date) {
    date.setHours(time.getHours());
    date.setMinutes(time.getMinutes());
    date.setSeconds(0);
    return date;
  }
  static getTodayRange(closeHour): Date[] {
    const endOfBusinessDayTime = new Date();
    const splittedCloseHour = closeHour.split(":");
    endOfBusinessDayTime.setHours(splittedCloseHour[0], 0, 0);
    const beginTime: Date = new Date(endOfBusinessDayTime);
    const endTime: Date = new Date(endOfBusinessDayTime);
    const currentDateBusinessTime = DateTimeManager.updateTimeOffsetBaseOnBusiness(new Date());
    if (DateTimeManager.compareDates(beginTime, currentDateBusinessTime) === -1) {
      // this mean the user still in the old busniess day
      beginTime.setDate(endTime.getDate() - 1);
    } else {
      // this mean the user has entered the new business day
      endTime.setDate(beginTime.getDate() + 1);
    }
    beginTime.setMinutes(0);
    endTime.setMinutes(0);
    beginTime.setSeconds(0);
    endTime.setSeconds(0);
    beginTime.setMilliseconds(0);
    endTime.setMilliseconds(0);
    return [beginTime, endTime];
  }
  static getDayRange(date, closeHour): Date[] {
    const endOfBusinessDayTime = new Date(date);
    const splittedCloseHour = closeHour.split(":");
    endOfBusinessDayTime.setHours(splittedCloseHour[0], 0, 0);
    const beginTime: Date = new Date(endOfBusinessDayTime);
    const endTime: Date = new Date(endOfBusinessDayTime);
    const currentDateBusinessTime = DateTimeManager.updateTimeOffsetBaseOnBusiness(new Date());
    if (DateTimeManager.compareDates(beginTime, currentDateBusinessTime) === -1) {
      // this mean the user still in the old busniess day
      beginTime.setDate(endTime.getDate() - 1);
    } else {
      // this mean the user has entered the new business day
      endTime.setDate(beginTime.getDate() + 1);
    }
    beginTime.setMinutes(0);
    endTime.setMinutes(0);
    beginTime.setSeconds(0);
    endTime.setSeconds(0);
    beginTime.setMilliseconds(0);
    endTime.setMilliseconds(0);
    return [beginTime, endTime];
  }
  static getDateRangeOfWeek(year, weekNumber) {
    const date = DateTimeManager.firstDayOfWeek(year, weekNumber);
    date.setMinutes(0);
    date.setMilliseconds(0);
    return [startOfWeek(date), endOfWeek(date)];
  }
  static getYesterdayRange(closeHour) {
    const dateRange: Date[] = DateTimeManager.getTodayRange(closeHour);
    if (isFirstDayOfMonth(dateRange[1])) {
      const lastDay = dateRange[1].getMonth() - 1;
      dateRange[1].setMonth(lastDay && lastDay === -1 ? 11 : lastDay || 0);
    }
    dateRange[1].setDate(dateRange[0].getDate());
    dateRange[0].setDate(dateRange[0].getDate() - 1);
    return dateRange;
  }
  static getThisWeekRange(closeHour) {
    const todayDateRange = DateTimeManager.getTodayRange(closeHour)[0];
    const weekDateRange: Date[] = [startOfWeek(todayDateRange), endOfWeek(todayDateRange)];
    weekDateRange[0].setHours(todayDateRange.getHours());
    weekDateRange[0].setMinutes(todayDateRange.getMinutes());
    weekDateRange[0].setSeconds(0);

    weekDateRange[1].setHours(todayDateRange.getHours());
    weekDateRange[1].setMinutes(todayDateRange.getMinutes());
    weekDateRange[1].setSeconds(0);
    weekDateRange[1].setDate(weekDateRange[1].getDate() + 1);
    return weekDateRange;
  }

  static getLastWeekRange(closeHour) {
    const endDateOfLastWeek: Date = DateTimeManager.getThisWeekRange(closeHour)[0];
    endDateOfLastWeek.setDate(endDateOfLastWeek.getDate() - 1);

    const lastWeekDateRange: Date[] = [startOfWeek(endDateOfLastWeek), endOfWeek(endDateOfLastWeek)];
    lastWeekDateRange[0].setHours(endDateOfLastWeek.getHours());
    lastWeekDateRange[0].setMinutes(endDateOfLastWeek.getMinutes());
    lastWeekDateRange[0].setSeconds(0);
    lastWeekDateRange[1].setHours(endDateOfLastWeek.getHours());
    lastWeekDateRange[1].setMinutes(endDateOfLastWeek.getMinutes());
    lastWeekDateRange[1].setSeconds(0);
    lastWeekDateRange[1].setDate(lastWeekDateRange[1].getDate() + 1);
    return lastWeekDateRange;
  }

  static getThisMonthRangeUntilToday(closeHour) {
    const todayDateRange = DateTimeManager.getTodayRange(closeHour)[0];
    const monthDateRange: Date[] = [startOfMonth(todayDateRange), todayDateRange];
    monthDateRange[0].setHours(todayDateRange.getHours());
    monthDateRange[0].setMinutes(todayDateRange.getMinutes());
    monthDateRange[0].setSeconds(0);
    monthDateRange[1].setHours(todayDateRange.getHours());
    monthDateRange[1].setMinutes(todayDateRange.getMinutes());
    monthDateRange[1].setSeconds(0);
    monthDateRange[1].setDate(monthDateRange[1].getDate() + 1);
    return monthDateRange;
  }

  static getThisMonthRange(closeHour) {
    const todayDateRange = DateTimeManager.getTodayRange(closeHour)[0];
    const monthDateRange: Date[] = [startOfMonth(todayDateRange), endOfMonth(todayDateRange)];
    monthDateRange[0].setHours(todayDateRange.getHours());
    monthDateRange[0].setMinutes(todayDateRange.getMinutes());
    monthDateRange[0].setSeconds(0);
    monthDateRange[1].setHours(todayDateRange.getHours());
    monthDateRange[1].setMinutes(todayDateRange.getMinutes());
    monthDateRange[1].setSeconds(0);
    monthDateRange[1].setDate(monthDateRange[1].getDate() + 1);
    return monthDateRange;
  }

  static getLastMonthRangeIrrespectiveCloseHour() {
    const today = new Date();
    today.setMonth(today.getMonth() - 1);
    const lastMonthDateRange: Date[] = [startOfMonth(today), endOfMonth(today)];
    return lastMonthDateRange;
  }

  static getThisMonthRangeUntilTodayIrrespectiveCloseHour() {
    const today = new Date();
    today.setHours(23);
    today.setMinutes(59);
    const monthDateRange: Date[] = [startOfMonth(new Date()), today];
    return monthDateRange;
  }

  static getLastMonthRange(closeHour) {
    const endDateOfLastMonth: Date = DateTimeManager.getTodayRange(closeHour)[0];
    endDateOfLastMonth.setDate(1);
    endDateOfLastMonth.setMonth(endDateOfLastMonth.getMonth() - 1);
    const lastMonthDateRange: Date[] = [startOfMonth(endDateOfLastMonth), endOfMonth(endDateOfLastMonth)];
    lastMonthDateRange[0].setHours(endDateOfLastMonth.getHours());
    lastMonthDateRange[0].setMinutes(endDateOfLastMonth.getMinutes());
    lastMonthDateRange[1].setHours(endDateOfLastMonth.getHours());
    lastMonthDateRange[1].setMinutes(endDateOfLastMonth.getMinutes());
    lastMonthDateRange[1].setSeconds(endDateOfLastMonth.getSeconds()); // or lastMonthDateRange[1].setSeconds(0)
    lastMonthDateRange[1].setDate(lastMonthDateRange[1].getDate() + 1);
    return lastMonthDateRange;
  }

  static getThisYearRange(closeHour) {
    const todayDateRange = DateTimeManager.getTodayRange(closeHour)[0];
    const yearDateRange: Date[] = [startOfYear(todayDateRange), endOfYear(todayDateRange)];
    yearDateRange[0].setHours(todayDateRange.getHours());
    yearDateRange[0].setMinutes(todayDateRange.getMinutes());
    yearDateRange[0].setSeconds(0);
    yearDateRange[1].setHours(todayDateRange.getHours());
    yearDateRange[1].setMinutes(todayDateRange.getMinutes());
    yearDateRange[1].setSeconds(0);
    yearDateRange[1].setDate(yearDateRange[1].getDate() + 1);
    return yearDateRange;
  }

  static getLastYearRange(closeHour) {
    const endDateOfLastMonth: Date = DateTimeManager.getTodayRange(closeHour)[0];
    endDateOfLastMonth.setFullYear(endDateOfLastMonth.getFullYear() - 1);

    const lastYearDateRange: Date[] = [startOfYear(endDateOfLastMonth), endOfYear(endDateOfLastMonth)];
    lastYearDateRange[0].setHours(endDateOfLastMonth.getHours());
    lastYearDateRange[0].setMinutes(endDateOfLastMonth.getMinutes());
    lastYearDateRange[0].setSeconds(0);
    lastYearDateRange[1].setHours(endDateOfLastMonth.getHours());
    lastYearDateRange[1].setMinutes(endDateOfLastMonth.getMinutes());
    lastYearDateRange[1].setSeconds(0);
    lastYearDateRange[1].setDate(lastYearDateRange[1].getDate() + 1);
    return lastYearDateRange;
  }
  static firstDayOfWeek(year, week) {
    // Jan 1 of 'year'
    const d = new Date(year, 0, 1);
    const offset = d.getTimezoneOffset();

    // Sunday is converted from 0 to 7
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));

    // 7 days * (week - overlapping first week)
    d.setTime(d.getTime() + 7 * 24 * 60 * 60 * 1000 * (week + (year === d.getFullYear() ? -1 : 0)));

    // daylight savings fix
    d.setTime(d.getTime() + (d.getTimezoneOffset() - offset) * 60 * 1000);

    // back to Monday (from Thursday) should be fixed to sunday
    d.setDate(d.getDate() - 3);

    d.setMinutes(0);
    d.setSeconds(0);
    d.setMilliseconds(0);

    return d;
  }
  static updateTimeOffsetBaseOnBusiness(date: Date): Date {
    const localOffset = Math.abs(date.getTimezoneOffset() / 60);
    if (DateTimeManager.getTimeOffsetForDate(date) !== localOffset) {
      date.setTime(date.getTime() + date.getTimezoneOffset() * 60000);
      date.setTime(date.getTime() + DateTimeManager.getTimeOffsetForDate(date) * 3600000);
    }
    return date;
  }
  static businessTime(date: Date): Date {
    const localOffset = Math.abs(date.getTimezoneOffset() / 60);
    if (DateTimeManager.getTimeOffsetForDate(date) !== localOffset) {
      date.setTime(date.getTime() + date.getTimezoneOffset() * 60000);
      date.setTime(date.getTime() + DateTimeManager.getTimeOffsetForDate(date) * 3600000);
    }
    return date;
  }

  static getLastXDays(days, closeHour): Date[] {
    const date = new Date();
    let lastXDayRange = new Date(date.getTime() - days * 24 * 60 * 60 * 1000);
    lastXDayRange = parse(
      format(new Date(lastXDayRange.toDateString() + " " + closeHour), "YYYY/MM/DD hh:mm A"),
      "",
      0,
      {}
    );

    lastXDayRange.setMinutes(0);
    lastXDayRange.setMilliseconds(0);
    lastXDayRange.setSeconds(0);
    date.setMinutes(0);
    date.setMilliseconds(0);
    date.setSeconds(0);

    return [
      DateTimeManager.updateTimeOffsetBaseOnBusiness(lastXDayRange),
      DateTimeManager.updateTimeOffsetBaseOnBusiness(date),
    ];
  }

  static lastXDays(days, closeHour): Date[] {
    const splittedCloseHour = closeHour.split(":");
    const date = new Date();
    date.setHours(splittedCloseHour[0], 0, 0);
    const currentDateBusinessTime = DateTimeManager.updateTimeOffsetBaseOnBusiness(new Date());
    if (DateTimeManager.compareDates(date, currentDateBusinessTime) === -1) {
      // this mean the user still in the old busniess day
      date.setDate(date.getDate() - 1);
    }
    const last = new Date(date.getTime() - days * 24 * 60 * 60 * 1000);
    last.setMinutes(0);
    last.setMilliseconds(0);
    last.setSeconds(0);
    date.setMinutes(0);
    date.setMilliseconds(0);
    date.setSeconds(0);
    return [last, date];
  }

  static compareDates(date1: Date, date2: Date) {
    return compareDesc(date1, date2);
  }
  static _updateTimeOffset(date: Date, offset: number) {
    const localOffset = Math.abs(date.getTimezoneOffset() / 60);
    if (offset !== localOffset) {
      date.setTime(date.getTime() + date.getTimezoneOffset() * 60000);
      date.setTime(date.getTime() + offset * 3600000);
    }
    return date;
  }

  static getSameDayPreviousWeek(day: Date, closeHour): Date[] {
    // const dayInWeek = dayRange[0].getDay();
    // newDate.setDate( newDate.getDate() - 1 )
    // const sameDayPrevWeek = new Date(dayRange);
    const startOfSameDayPrevWeek = new Date();
    startOfSameDayPrevWeek.setDate(day.getDate() - 7);
    const endOfSameDayPrevWeek = new Date(startOfSameDayPrevWeek);
    endOfSameDayPrevWeek.setDate(endOfSameDayPrevWeek.getDate() + 1);
    return DateTimeManager.getDateRangeInCloseHours([startOfSameDayPrevWeek, endOfSameDayPrevWeek], closeHour);
  }

  static isLeapYear(day: Date): boolean {
    const year = day.getFullYear();
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  }

  static getSameDayPreviousYear(day: Date, closeHour): Date[] {
    const daysToShift = DateTimeManager.isLeapYear(day) ? 2 : 1;
    const startOfSameDayPrevYear = new Date(day);
    startOfSameDayPrevYear.setFullYear(startOfSameDayPrevYear.getFullYear() - 1);
    startOfSameDayPrevYear.setDate(startOfSameDayPrevYear.getDate() + daysToShift);
    const endOfSameDayPrevYear = new Date(startOfSameDayPrevYear);
    endOfSameDayPrevYear.setDate(endOfSameDayPrevYear.getDate() + 1);
    return DateTimeManager.getDateRangeInCloseHours([startOfSameDayPrevYear, endOfSameDayPrevYear], closeHour);
  }

  static getSameWeekPreviousYear(day: Date, closeHour) {
    const weekNumber = getISOWeek(day);
    const startOfSameWeekPrevYear = new Date(day.getFullYear() - 1, 0, weekNumber * 7);
    const endOfSameWeekPrevYear = new Date(startOfSameWeekPrevYear);
    endOfSameWeekPrevYear.setDate(endOfSameWeekPrevYear.getDate() + 7);

    return DateTimeManager.getDateRangeInCloseHours([startOfSameWeekPrevYear, endOfSameWeekPrevYear], closeHour);
  }

  static getDateRangeInCloseHours(dateRange: Date[], closeHour) {
    const todayRange: Date = DateTimeManager.getTodayRange(closeHour)[0];
    dateRange[0].setHours(todayRange.getHours());
    dateRange[1].setHours(todayRange.getHours());
    dateRange[0].setMinutes(0);
    dateRange[0].setSeconds(0);
    dateRange[1].setMinutes(0);
    dateRange[1].setSeconds(0);
    return dateRange;
  }

  static getMonthNumberFromString(month: string) {
    const months = [
      { label: "January", value: 0 },
      { label: "February", value: 1 },
      { label: "March", value: 2 },
      { label: "April", value: 3 },
      { label: "May", value: 4 },
      { label: "June", value: 5 },
      { label: "July", value: 6 },
      { label: "August", value: 7 },
      { label: "September", value: 8 },
      { label: "October", value: 9 },
      { label: "November", value: 10 },
      { label: "December", value: 11 },
    ];
    const filteredMonthObj = months.filter((monthObj) => monthObj.label === month)[0];
    return filteredMonthObj && filteredMonthObj.value;
  }

  static getSingleDateWithBusinessTiming(date: Date) {
    return format(DateTimeManager.updateTimeOffsetBaseOnBusiness(date), "yyyy-MM-dd");
  }
}
